import type { Node, Edge } from "@xyflow/react";
import type { MergeEntry } from "./types";

type WorkflowEdge = Edge & {
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
};

interface AncestorValidationResult {
  isValid: boolean;
  commonAncestor?: string;
  invalidNodes?: string[];
  errorMessage?: string;
}

export class CommonAncestorValidator {
  static validateCommonAncestor(
    entries: MergeEntry[],
    nodes: Node[],
    edges: WorkflowEdge[],
  ): AncestorValidationResult {
    if (entries.length < 2) {
      return {
        isValid: true,
        errorMessage:
          "Se requieren al menos 2 nodos para validar ancestro común",
      };
    }

    const nodePaths = new Map<string, string[]>();
    const invalidNodes: string[] = [];

    entries.forEach((entry) => {
      const node = nodes.find((n) => n.id === entry.nodeId);
      if (!node) {
        invalidNodes.push(entry.nodeId);
        return;
      }
      const path = this.getAncestorPath(entry.nodeId, edges);
      nodePaths.set(entry.nodeId, path);
    });

    if (invalidNodes.length > 0) {
      return {
        isValid: false,
        invalidNodes,
        errorMessage: `Uno o más nodos seleccionados ya no están disponibles.`,
      };
    }

    const paths = Array.from(nodePaths.values());

    const reversedPaths = paths.map((p) => [...p].reverse());

    let commonAncestor: string | undefined = undefined;
    const minLength = Math.min(...reversedPaths.map((p) => p.length));

    for (let i = 0; i < minLength; i++) {
      const current = reversedPaths[0][i];
      if (reversedPaths.every((p) => p[i] === current)) {
        commonAncestor = current;
      } else {
        break;
      }
    }

    if (!commonAncestor) {
      return {
        isValid: false,
        invalidNodes: entries.map((e) => e.nodeId),
        errorMessage:
          "Los nodos seleccionados deben compartir un ancestro común (punto de división)",
      };
    }

    return {
      isValid: true,
      commonAncestor,
    };
  }

  static findDirectParent(
    nodeId: string,
    edges: WorkflowEdge[],
  ): string | undefined {
    const incomingEdge = edges.find((edge) => edge.target === nodeId);
    return incomingEdge?.source;
  }

  static getNodesWithSameParent(
    referenceNodeId: string,
    nodeType: string,
    _nodes: Node[],
    edges: WorkflowEdge[],
  ): string[] {
    const referenceParent = this.findDirectParent(referenceNodeId, edges);

    return _nodes
      .filter(
        (node) =>
          (node.data as { type?: string })?.type === nodeType &&
          node.id !== referenceNodeId &&
          this.findDirectParent(node.id, edges) === referenceParent,
      )
      .map((node) => node.id);
  }

  static canAddNodeToMerge(
    newNodeId: string,
    existingEntries: MergeEntry[],
    nodes: Node[],
    edges: WorkflowEdge[],
  ): { canAdd: boolean; reason?: string } {
    if (existingEntries.length === 0) {
      return { canAdd: true };
    }

    const testEntries = [
      ...existingEntries,
      { nodeId: newNodeId, name: "test", type: "MERGE_ENTRY" as const },
    ];
    const validation = this.validateCommonAncestor(testEntries, nodes, edges);

    if (!validation.isValid) {
      return {
        canAdd: false,
        reason:
          validation.errorMessage ||
          "El nodo no comparte un ancestro común con los demás",
      };
    }

    return { canAdd: true };
  }

  static getAncestorPath(nodeId: string, edges: WorkflowEdge[]): string[] {
    const path: string[] = [];
    let currentId: string | undefined = nodeId;

    while (currentId) {
      const parent = this.findDirectParent(currentId, edges);
      if (!parent) break;

      path.push(parent);
      currentId = parent;
    }

    return path;
  }

  static validateSameBranch(
    entries: MergeEntry[],
    _nodes: Node[],
    edges: WorkflowEdge[],
  ): AncestorValidationResult {
    if (entries.length < 2) {
      return { isValid: true };
    }

    const nodePaths = new Map<string, string[]>();

    entries.forEach((entry) => {
      const path = this.getAncestorPath(entry.nodeId, edges);
      nodePaths.set(entry.nodeId, path);
    });

    const paths = Array.from(nodePaths.values());
    const commonPath = this.findCommonPath(paths);

    if (commonPath.length === 0) {
      return {
        isValid: false,
        invalidNodes: entries.map((e) => e.nodeId),
        errorMessage: "Los nodos no comparten un ancestro común",
      };
    }

    return {
      isValid: true,
      commonAncestor: commonPath[commonPath.length - 1],
    };
  }

  private static findCommonPath(paths: string[][]): string[] {
    if (paths.length === 0) return [];
    if (paths.length === 1) return paths[0];

    const commonPath: string[] = [];
    const minLength = Math.min(...paths.map((path) => path.length));

    for (let i = 0; i < minLength; i++) {
      const currentElement = paths[0][i];
      const allMatch = paths.every((path) => path[i] === currentElement);

      if (allMatch) {
        commonPath.push(currentElement);
      } else {
        break;
      }
    }

    return commonPath;
  }
  static isDownstream(
    sourceId: string,
    targetId: string,
    edges: WorkflowEdge[],
  ): boolean {
    const visited = new Set<string>();
    const queue = [sourceId];

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (current === targetId) return true;
      if (visited.has(current)) continue;
      visited.add(current);

      const outgoing = edges
        .filter((edge) => edge.source === current)
        .map((edge) => edge.target);

      queue.push(...outgoing);
    }

    return false;
  }
}
